import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useAuth } from "../providers/AuthProvider";

import plusIcon from "../images/plus.png";

import DashRevenue from "../images/dash-revenue.png";
import DashPayment from "../images/dash-payment.png";
import DashInvoice from "../images/dash-invoice.png";
import DashWallet from "../images/dash-wallet.png";
import DashSales from "../images/dash-sales.png";
import DashProducts from "../images/dash-products.png";
import DashCustomers from "../images/dash-customers.png";
// import PlayStore from "../images/play-store.png";
// import AppStore from "../images/app-store.png";
import { cn, getCustomizationValue, renderCurrency, renderNumber, toChartSeries } from "src/lib/utils";
import CustomDataTable from "src/components/CustomDataTable";
import { dashboard } from "src/api";
import PermissionWrapper from "src/components/PermissionWrapper";
import { useQueries } from "@tanstack/react-query";
import { viewWalletBalance } from "src/api/wallet";
import useHasPermissions from "src/hooks/useHasPermissions";
import { useMemo } from "react";
import { Chart } from "react-charts";
import ResizableBox from "src/components/ResizableBox";
import CustomModal from "src/components/CustomModal";
import NewCustomerModal from "src/components/modals/NewCustomerModal";
import TopUpWallet from "src/components/modals/TopUpWallet";

const customStyles = {
	table: {
		style: {
			paddingTop: "0.25rem",
		},
	},
	headRow: {
		style: {
			paddingInline: 0,
		},
	},
	rows: {
		style: {
			paddingInline: 0,
		},
	},
};

const Dashboard = () => {
	const { user } = useAuth();
	const navigate = useNavigate();
	const canViewWalletBalance = useHasPermissions("view_wallet_balance");

	const [{ data }, { data: rawWalletBalance }] = useQueries({
		queries: [
			{
				queryKey: ["dashboard"],
				queryFn: dashboard,
				suspense: true,
			},
			{
				queryKey: ["wallet-balance"],
				queryFn: viewWalletBalance,
				enabled: canViewWalletBalance,
				suspense: true,
			},
		],
	});

	const invoiceColumns = [
		{ name: cn(getCustomizationValue(user, "customer_label", true), "Name"), selector: (row) => `${row.first_name} ${row.last_name}` },
		{ name: "Date Created", selector: (row) => format(new Date(row.created_at), "MMM dd, yyyy") },
		{ name: "Total Amount", selector: (row) => renderCurrency(row.total_amount) },
	];
	const paymentsColumns = [
		{ name: getCustomizationValue(user, "customer_label", true), selector: (row) => `${row.first_name} ${row.last_name}` },
		{ name: "Amount", selector: (row) => renderCurrency(row.amount) },
		{ name: "Date Created", selector: (row) => format(new Date(row.created_at), "MMM dd, yyyy") },
	];
	const invoices = data?.data?.invoices ?? [];
	const payments = data?.data?.recent_payments ?? [];
	const unpaidInvoices = data?.data?.account_receivable ?? 0;
	const paymentsReceived = data?.data?.payment_received ?? 0;
	const cumulativeRevenue = data?.data?.cumulative_revenue ?? 0;
	const newCustomers = data?.data?.new_customers_count ?? 0;
	const productSold = data?.data?.total_products_sold ?? 0;
	const totalSales = data?.data?.total_sales_count ?? 0;
	const walletBalance = rawWalletBalance?.data;

	return (
		<div className="pt-2 dashContainer tw-grid tw-gap-9">
			<div className="tw-flex tw-justify-between tw-items-center tw-gap-x-10 tw-gap-y-4 tw-flex-wrap">
				<h2 className="tw-font-semibold tw-text-[18px]">Welcome, {user?.first_name || "User"}</h2>
				<button
					className="top-link"
					onClick={() => {
						if (!user?.bvn_verified) return navigate("/account");
						navigate("/invoices/add");
					}}
				>
					<img src={plusIcon} alt={cn("Create ", getCustomizationValue(user, "invoice_label", true))} /> Create New{" "}
					{getCustomizationValue(user, "invoice_label", true)}
				</button>
			</div>
			<PermissionWrapper permission="view_dashboard">
				<div className="tw-grid tw-gap-4 tw-grid-cols-[repeat(auto-fit,minmax(250px,1fr))]">
					<Card title="Wallet Balance" amount={renderCurrency(walletBalance)} img={DashWallet} color="green" />

					<Card title="Total Unpaid Invoice" amount={renderCurrency(unpaidInvoices)} img={DashInvoice} color="blue" />

					<Card title="Total Payments Received" amount={renderCurrency(paymentsReceived)} img={DashPayment} color="purple" />

					<Card title="Cumulative Revenue" amount={renderCurrency(cumulativeRevenue)} img={DashRevenue} color="gray" />
				</div>
			</PermissionWrapper>
			<div className="tw-grid tw-gap-4 tw-grid-cols-[repeat(auto-fit,minmax(275px,1fr))]">
				<div className="tw-px-5 tw-py-5 tw-rounded-lg tw-flex tw-justify-between tw-items-center tw-bg-[#06793D0F] tw-text-[#06793D]">
					<div className="tw-flex tw-items-center tw-font-medium tw-gap-2">
						<img src={DashSales} alt="Sales" />
						Sales
					</div>
					<p className="tw-text-xl tw-font-bold">{renderNumber(totalSales)}</p>
				</div>
				<div className="tw-px-5 tw-py-5 tw-rounded-lg tw-flex tw-justify-between tw-items-center tw-bg-[#00B2FF0F] tw-text-[#00B2FF]">
					<div className="tw-flex tw-items-center tw-font-medium tw-gap-2">
						<img src={DashProducts} alt="Products Sold" />
						Products Sold
					</div>
					<p className="tw-text-xl tw-font-bold">{renderNumber(productSold)}</p>
				</div>
				<div className="tw-px-5 tw-py-5 tw-rounded-lg tw-flex tw-justify-between tw-items-center tw-bg-[#E379000F] tw-text-[#E37900]">
					<div className="tw-flex tw-items-center tw-font-medium tw-gap-2">
						<img src={DashCustomers} alt="New Customers" />
						New Customers
					</div>
					<p className="tw-text-xl tw-font-bold">{renderNumber(newCustomers)}</p>
				</div>
			</div>
			<div className="tw-grid md:tw-grid-cols-[1fr,300px] tw-gap-4">
				<ChartComp monthlyData={data?.data?.monthly_overview ?? []} />
				<QuickLinks />
			</div>
			<div className="tw-grid tw-gap-6 tw-grid-cols-[repeat(auto-fit,minmax(300px,1fr))]">
				<PermissionWrapper permission="view_invoices">
					<div className="dashSect4-left">
						<div className="dash-tables">
							<div className="table-header">
								<h4>{getCustomizationValue(user, "invoice_label")}</h4>
								<div className="view-more-btn">
									<Link to="/invoices" className="blue-btn">
										View all
									</Link>
								</div>
							</div>
							<CustomDataTable
								customStyles={customStyles}
								columns={invoiceColumns}
								data={invoices}
								pagination={false}
								containerClassName="tw-shadow-none"
							/>
						</div>
					</div>
				</PermissionWrapper>
				<PermissionWrapper permission="view_payment">
					<div className="dashSect4-right">
						<div className="mb-4 dash-tables">
							<div className="table-header">
								<h4>Payments</h4>
								<div className="view-more-btn">
									<Link to="/payments" className="green-btn">
										View all
									</Link>
								</div>
							</div>
							<CustomDataTable
								customStyles={customStyles}
								columns={paymentsColumns}
								data={payments}
								pagination={false}
								containerClassName="tw-shadow-none"
							/>
						</div>

						{/* <div className="dash-app-downloads tw-flex-wrap tw-justify-between tw-gap-y-4">
							<h3>Download The Mobile App</h3>
							<div className="tw-flex tw-items-center tw-gap-4 tw-flex-wrap tw-w-fit">
								<img className="flex-1" src={AppStore} alt="App Store" />
								<img className="flex-1" src={PlayStore} alt="Play Store" />
							</div>
						</div> */}
					</div>
				</PermissionWrapper>
			</div>
		</div>
	);
};

const QuickLinks = () => {
	const navigate = useNavigate();
	const { user } = useAuth();

	return (
		<>
			<div class="tw-rounded-2xl tw-h-fit tw-border tw-border-gray-200 tw-bg-white tw-p-6 tw-grid tw-gap-3">
				<h2 class="tw-text-lg tw-font-semibold tw-text-gray-900">Quick Actions</h2>

				<CustomModal
					title={cn("Add Single ", getCustomizationValue(user, "customer_label", true))}
					trigger={
						<button class="tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-xl tw-bg-gray-50 tw-p-2 tw-bg-blue-50 tw-text-blue-900 hover:tw-bg-blue-100">
							<span class="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-text-blue-900">
								<svg class="tw-h-5 tw-w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										d="M17 20h5v-2a4 4 0 00-4-4h-1M9 20H4v-2a4 4 0 014-4h1m4-4a4 4 0 100-8 4 4 0 000 8zm6 2a3 3 0 100-6 3 3 0 000 6z"
									/>
								</svg>
							</span>
							<span class="tw-font-medium">Add new customer</span>
						</button>
					}
				>
					<NewCustomerModal />
				</CustomModal>

				<button
					onClick={() => {
						if (!user?.bvn_verified) return navigate("/account");
						navigate("/invoices/add");
					}}
					class="tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-xl tw-bg-gray-50 tw-p-2 tw-bg-blue-50 tw-text-blue-900 hover:tw-bg-blue-100"
				>
					<span class="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center">
						<svg class="tw-h-5 tw-w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5l5 5v11a2 2 0 01-2 2z"
							/>
						</svg>
					</span>
					<span class="tw-font-medium">Create invoice</span>
				</button>

				<button
					onClick={() => navigate("/customers")}
					class="tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-xl tw-bg-gray-50 tw-p-2 tw-bg-blue-50 tw-text-blue-900 hover:tw-bg-blue-100"
				>
					<span class="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center">
						<svg class="tw-h-5 tw-w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
							/>
						</svg>
					</span>
					<span class="tw-font-medium">View all customers</span>
				</button>

				<CustomModal
					title="Top Up Wallet"
					trigger={
						<button class="tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-xl tw-bg-gray-50 tw-p-2 tw-bg-blue-50 tw-text-blue-900 hover:tw-bg-blue-100">
							<span class="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center">
								<svg class="tw-h-5 tw-w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
									<path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
								</svg>
							</span>
							<span class="tw-font-medium">Top-up Wallet</span>
						</button>
					}
				>
					<TopUpWallet />
				</CustomModal>
			</div>
		</>
	);
};

const ChartComp = ({ monthlyData }) => {
	const primaryAxis = useMemo(() => ({ getValue: (datum) => datum.primary }), []);

	const secondaryAxes = useMemo(() => [{ getValue: (datum) => datum.secondary }], []);

	const data = toChartSeries(monthlyData);

	return (
		<ResizableBox>
			<Chart
				options={{
					data,
					primaryAxis,
					secondaryAxes,
					defaultColors: ["#06793D", "#002366"],
				}}
			/>
		</ResizableBox>
	);
};

const Card = ({ title, amount, img, color }) => (
	<div className={`dashSect2-cards ${color}`}>
		<div className="tw-flex tw-items-center tw-justify-between">
			<p>{title}</p>
			<div className="card-top-img">
				<img src={img} alt={title} />
			</div>
		</div>
		<h3 className="tw-mt-5 tw-text-2xl tw-font-bold">{amount}</h3>
	</div>
);

export default Dashboard;
